// @flow
import {useCallback} from 'react';
import {useSelector} from 'react-redux';
import {useActions} from '../../../../../utils/globalStore';
import {actions} from '../duck';
import type {RootState} from '../../../../';
import type {PickupRateRequest} from '../../../../../entities';

export function useEnabledLogisticPickup() {
  const enabledLogisticPickupSelector = (state: RootState) =>
    state.logisticPickupConfig.pickupMethod;
  const pickupMethod = useSelector(enabledLogisticPickupSelector);
  const fetchLogisticPickupAction = useActions(actions.getEnabledPickupMethod);

  const fetchLogisticPickup = useCallback(() => {
    fetchLogisticPickupAction();
  }, [fetchLogisticPickupAction]);

  return {
    pickupMethod,
    fetchLogisticPickup,
  };
}

// Available countries
const availableShippingCountriesSelector = (state: RootState) =>
  state.logisticShippingConfig.availableShippingCountries;

export function useGetAvailableShippingCountries() {
  return useSelector(availableShippingCountriesSelector);
}

const currencyCountry = (state: RootState) => state.currency.currency;

export function useGetCurrencyCountry() {
  return useSelector(currencyCountry);
}

export function useCreateLogisticPickupRate() {
  const isSaving = useSelector(
    (state: RootState) => state.logisticPickupConfig.isSaving
  );
  const savingSuccess = useSelector(
    (state: RootState) => state.logisticPickupConfig.savingPickupSuccess
  );
  const savingError = useSelector(
    (state: RootState) => state.logisticPickupConfig.savingPickupError
  );
  const savePickupMethod = useActions(actions.savePickupMethod);

  const createPickupRate = (pickupRateRequest: PickupRateRequest) => {
    return savePickupMethod({pickupRateRequest});
  };

  return {
    isSaving,
    savingSuccess,
    savingError,
    createPickupRate,
  };
}

export function useUpdateLogisticPickupRate() {
  const isSaving = useSelector(
    (state: RootState) => state.logisticPickupConfig.isSaving
  );
  const savingSuccess = useSelector(
    (state: RootState) => state.logisticPickupConfig.savingPickupSuccess
  );
  const savingError = useSelector(
    (state: RootState) => state.logisticPickupConfig.savingPickupError
  );

  const updatePickupMethod = useActions(actions.updatePickupRate);

  const updatePickupRate = (pickupRateRequest: PickupRateRequest) => {
    const {adminRateId, ...rest} = pickupRateRequest;
    return updatePickupMethod({
      adminRateId,
      pickupRateRequest: rest,
    });
  };

  return {
    isSaving,
    savingSuccess,
    savingError,
    updatePickupRate,
  };
}

export function useDeleteLogisticPickupRate() {
  const isDeleting = useSelector(
    (state: RootState) => state.logisticPickupConfig.isSaving
  );
  const deleteSuccess = useSelector(
    (state: RootState) => state.logisticPickupConfig.deletePickupSuccess
  );
  const deleteError = useSelector(
    (state: RootState) => state.logisticPickupConfig.deletePickupError
  );
  const messageError = useSelector(
    (state: RootState) => state.logisticPickupConfig.messageError
  );
  const deletePickupRate = useActions(actions.deletePickupRate);

  const deleteRate = (adminRateId: string) => {
    return deletePickupRate({adminRateId});
  };

  return {
    isDeleting,
    deleteSuccess,
    deleteError,
    messageError,
    deleteRate,
  };
}

export function useGetLogisticPickupRate() {
  const isFetching = useSelector(
    (state: RootState) => state.logisticPickupConfig.isFetchingPickupMethods
  );
  const fetchingSuccess = useSelector(
    (state: RootState) => state.logisticPickupConfig.fetchingPickupSuccess
  );
  const fetchingError = useSelector(
    (state: RootState) => state.logisticPickupConfig.fetchingPickupError
  );
  const pickupRate = useSelector(
    (state: RootState) => state.logisticPickupConfig.pickupRate
  );
  const fetchPickupRate = useActions(actions.getPickupRate);

  const fetchRate = useCallback(
    (adminRateId: string) => {
      return fetchPickupRate({adminRateId});
    },
    [fetchPickupRate]
  );

  return {
    isFetching,
    fetchingSuccess,
    fetchingError,
    fetchRate,
    pickupRate,
  };
}

export function useResetState() {
  const resetStateAction = useActions(actions.resetState);

  const resetState = useCallback(() => {
    return resetStateAction();
  }, [resetStateAction]);

  return {resetState};
}

export function useUpdateEnabledPickupMethod() {
  const isSaving = useSelector(
    (state: RootState) => state.logisticPickupConfig.isSaving
  );
  const savingEnabledSuccess = useSelector(
    (state: RootState) => state.logisticPickupConfig.savingPickupEnabledSuccess
  );
  const savingEnabledError = useSelector(
    (state: RootState) => state.logisticPickupConfig.savingPickupEnabledError
  );
  const messageError = useSelector(
    (state: RootState) => state.logisticPickupConfig.messageError
  );
  const fetchUpdatePickupMethod = useActions(actions.enabledPickupMethod);

  const updatePickupMethod = (enabled: boolean) => {
    return fetchUpdatePickupMethod({enabled});
  };

  return {
    messageError,
    isSaving,
    savingEnabledSuccess,
    savingEnabledError,
    updatePickupMethod,
  };
}
